import React, { useMemo } from "react"
import Breadcrumb from "components/Common/Breadcrumb"
import DashboardLoader from "components/Loader/DashboardLoader"
import { Row, Col, Card, CardBody } from "reactstrap"
import {
  useGetAllClientsQuery,
  useGetAllProjectsQuery,
} from "store/features/client/clientManagementApi"
import { useGetAllFixedCostsQuery } from "store/features/fixedCosts/fixedCostsManagementApi"
import { useGetAllTransactionsQuery } from "store/features/transaction/transactionManagementApi"
import { useGetAllClientLeadsQuery } from "store/features/clientLeads/clientLeadsManagement"
import OngoingProjects from "./OngoingProjects"
import HighPriorityLeads from "./HighPriorityLeads"
// import {
//   useGetAllClientsQuery,
//   useGetAllFixedCostsQuery,
//   useGetAllTransactionsQuery,
//   useGetAllProjectsQuery,
//   useGetAllClientLeadsQuery
// } from "store/features/client/clientManagementApi"

const AdminDashboard = () => {
  const { data: clients = [], isLoading: clientsLoading } =
    useGetAllClientsQuery()
  const { data: fixedCosts = [], isLoading: fixedCostsLoading } =
    useGetAllFixedCostsQuery()
  const { data: transactions = [], isLoading: transactionsLoading } =
    useGetAllTransactionsQuery()
  const { data: projects = [], isLoading: projectsLoading } =
    useGetAllProjectsQuery()
  const { data: clientLeads = [], isLoading: clientLeadsLoading } =
    useGetAllClientLeadsQuery()

  const isLoading =
    clientsLoading ||
    fixedCostsLoading ||
    transactionsLoading ||
    projectsLoading ||
    clientLeadsLoading

  // Get last 3 months names
  const getLastThreeMonths = () => {
    const months = []
    const date = new Date()

    for (let i = 0; i < 3; i++) {
      const monthDate = new Date(date.getFullYear(), date.getMonth() - i, 1)
      const monthName = monthDate.toLocaleString("default", { month: "long" })
      const year = monthDate.getFullYear()
      const month = monthDate.getMonth() + 1

      months.push({
        name: monthName,
        year: year,
        month: month,
        key: `${year}-${month.toString().padStart(2, "0")}`,
      })
    }

    return months.reverse()
  }

  // Calculate clients by month
  const clientsByMonth = useMemo(() => {
    const months = getLastThreeMonths()
    const result = {}

    months.forEach(month => {
      result[month.name] = clients.filter(client => {
        const clientDate = new Date(client.systemdate)
        return (
          clientDate.getFullYear() === month.year &&
          clientDate.getMonth() + 1 === month.month
        )
      }).length
    })

    return result
  }, [clients])

  // Calculate fixed costs by month
  const fixedCostsByMonth = useMemo(() => {
    const months = getLastThreeMonths()
    const result = {}

    months.forEach(month => {
      const monthCosts = fixedCosts.filter(cost => {
        if (!cost.date) return false
        const costDate = new Date(cost.date)
        return (
          costDate.getFullYear() === month.year &&
          costDate.getMonth() + 1 === month.month
        )
      })

      result[month.name] = monthCosts.reduce(
        (sum, cost) => sum + parseFloat(cost.amount || 0),
        0
      )
    })

    return result
  }, [fixedCosts])

  // Calculate transactions by month
  const transactionsByMonth = useMemo(() => {
    const months = getLastThreeMonths()
    const earningResult = {}
    const depositResult = {}
    const costResult = {}

    months.forEach(month => {
      const monthTransactions = transactions.filter(transaction => {
        const transactionDate = new Date(transaction.date)
        return (
          transactionDate.getFullYear() === month.year &&
          transactionDate.getMonth() + 1 === month.month
        )
      })

      earningResult[month.name] = monthTransactions
        .filter(t => t.transaction_type === "earning")
        .reduce((sum, t) => sum + parseFloat(t.amount || 0), 0)

      depositResult[month.name] = monthTransactions
        .filter(t => t.transaction_type === "deposit")
        .reduce((sum, t) => sum + parseFloat(t.amount || 0), 0)

      costResult[month.name] = monthTransactions
        .filter(t => t.transaction_type === "cost")
        .reduce((sum, t) => sum + parseFloat(t.amount || 0), 0)
    })

    return { earning: earningResult, deposit: depositResult, cost: costResult }
  }, [transactions])

  // Calculate receivable amount
  const receivableAmount = useMemo(() => {
    return projects.reduce((total, project) => {
      const budget = parseFloat(project.budget || 0)
      const totalPaid =
        project.payment?.reduce(
          (sum, payment) => sum + parseFloat(payment.amount || 0),
          0
        ) || 0
      return total + (budget - totalPaid)
    }, 0)
  }, [projects])

  // Calculate client leads by month
  const clientLeadsByMonth = useMemo(() => {
    const months = getLastThreeMonths()
    const result = {}

    months.forEach(month => {
      result[month.name] = clientLeads.filter(lead => {
        const leadDate = new Date(lead.systemdate)
        return (
          leadDate.getFullYear() === month.year &&
          leadDate.getMonth() + 1 === month.month
        )
      }).length
    })

    return result
  }, [clientLeads])

  const lastThreeMonths = getLastThreeMonths()

  if (isLoading) {
    return <DashboardLoader />
  }

  return (
    <div className="page-content">
      <div className="container-fluid">
        <Breadcrumb title="Dashboard" breadcrumbItem="Overview" />

        {/* First Row - 3 sections */}
        <Row className="mb-4">
          {/* Clients Section */}
          <Col lg={4}>
            <Card className="dashboard-card">
              <CardBody>
                <div className="d-flex align-items-center">
                  <div className="flex-grow-1">
                    <h4 className="card-title mb-3">Clients Created</h4>
                    <div className="month-stats">
                      {lastThreeMonths?.reverse().map(month => (
                        <div
                          key={month.name}
                          className="d-flex justify-content-between align-items-center mb-2"
                        >
                          <span className="text-muted">{month.name}</span>
                          <span className="fw-bold text-primary fs-5">
                            {clientsByMonth[month.name] || 0}
                          </span>
                        </div>
                      ))}
                    </div>
                  </div>
                  {/* <div className="flex-shrink-0">
                    <div className="avatar-sm rounded-circle bg-primary bg-soft">
                      <i className="fas fa-users fa-2x text-primary"></i>
                    </div>
                  </div> */}
                </div>
              </CardBody>
            </Card>
          </Col>

          {/* Fixed Costs Section */}
          <Col lg={4}>
            <Card className="dashboard-card">
              <CardBody>
                <div className="d-flex align-items-center">
                  <div className="flex-grow-1">
                    <h4 className="card-title mb-3">Fixed Costs</h4>
                    <div className="month-stats">
                      {lastThreeMonths.map(month => (
                        <div
                          key={month.name}
                          className="d-flex justify-content-between align-items-center mb-2"
                        >
                          <span className="text-muted">{month.name}</span>
                          <span className="fw-bold text-danger fs-5">
                            {(
                              fixedCostsByMonth[month.name] || 0
                            ).toLocaleString()}
                          </span>
                        </div>
                      ))}
                    </div>
                  </div>
                  {/* <div className="flex-shrink-0">
                    <div className="avatar-sm rounded-circle bg-danger bg-soft">
                      <i className="fas fa-money-bill-wave fa-2x text-danger"></i>
                    </div>
                  </div> */}
                </div>
              </CardBody>
            </Card>
          </Col>

          {/* Client Leads Section */}
          <Col lg={4}>
            <Card className="dashboard-card">
              <CardBody>
                <div className="d-flex align-items-center">
                  <div className="flex-grow-1">
                    <h4 className="card-title mb-3">Client Leads</h4>
                    <div className="month-stats">
                      {lastThreeMonths.map(month => (
                        <div
                          key={month.name}
                          className="d-flex justify-content-between align-items-center mb-2"
                        >
                          <span className="text-muted">{month.name}</span>
                          <span className="fw-bold text-warning fs-5">
                            {clientLeadsByMonth[month.name] || 0}
                          </span>
                        </div>
                      ))}
                    </div>
                  </div>
                  {/* <div className="flex-shrink-0">
                    <div className="avatar-sm rounded-circle bg-warning bg-soft">
                      <i className="fas fa-list-alt fa-2x text-warning"></i>
                    </div>
                  </div> */}
                </div>
              </CardBody>
            </Card>
          </Col>
        </Row>

        {/* Second Row - 3 sections */}
        <Row className="mb-4">
          <Col lg={4}>
            <Card className="dashboard-card">
              <CardBody>
                <div className="d-flex align-items-center">
                  <div className="flex-grow-1">
                    <h4 className="card-title mb-3 text-success">Deposit</h4>
                    <div className="month-stats">
                      {lastThreeMonths.map(month => (
                        <div
                          key={month.name}
                          className="d-flex justify-content-between align-items-center mb-2"
                        >
                          <span className="text-muted">{month.name}</span>
                          <span className="fw-bold text-success fs-5">
                            {(
                              transactionsByMonth.deposit[month.name] || 0
                            ).toLocaleString()}
                          </span>
                        </div>
                      ))}
                    </div>
                  </div>
                  {/* <div className="flex-shrink-0">
                    <div className="avatar-sm rounded-circle bg-success bg-soft">
                      <i className="fas fa-chart-line fa-2x text-success"></i>
                    </div>
                  </div> */}
                </div>
              </CardBody>
            </Card>
          </Col>

          {/* Transactions - Earning Section */}
          <Col lg={4}>
            <Card className="dashboard-card">
              <CardBody>
                <div className="d-flex align-items-center">
                  <div className="flex-grow-1">
                    <h4 className="card-title mb-3 text-success">Earnings</h4>
                    <div className="month-stats">
                      {lastThreeMonths.map(month => (
                        <div
                          key={month.name}
                          className="d-flex justify-content-between align-items-center mb-2"
                        >
                          <span className="text-muted">{month.name}</span>
                          <span className="fw-bold text-success fs-5">
                            {(
                              transactionsByMonth.earning[month.name] || 0
                            ).toLocaleString()}
                          </span>
                        </div>
                      ))}
                    </div>
                  </div>
                  {/* <div className="flex-shrink-0">
                    <div className="avatar-sm rounded-circle bg-success bg-soft">
                      <i className="fas fa-chart-line fa-2x text-success"></i>
                    </div>
                  </div> */}
                </div>
              </CardBody>
            </Card>
          </Col>

          {/* Transactions - Cost Section */}
          <Col lg={4}>
            <Card className="dashboard-card">
              <CardBody>
                <div className="d-flex align-items-center">
                  <div className="flex-grow-1">
                    <h4 className="card-title mb-3 text-danger">Costs</h4>
                    <div className="month-stats">
                      {lastThreeMonths.map(month => (
                        <div
                          key={month.name}
                          className="d-flex justify-content-between align-items-center mb-2"
                        >
                          <span className="text-muted">{month.name}</span>
                          <span className="fw-bold text-danger fs-5">
                            {(
                              transactionsByMonth.cost[month.name] || 0
                            ).toLocaleString()}
                          </span>
                        </div>
                      ))}
                    </div>
                  </div>
                  {/* <div className="flex-shrink-0">
                    <div className="avatar-sm rounded-circle bg-danger bg-soft">
                      <i className="fas fa-money-bill-wave fa-2x text-danger"></i>
                    </div>
                  </div> */}
                </div>
              </CardBody>
            </Card>
          </Col>
        </Row>

        <Row className="mb-4">
          {/* Receivable Amount Section */}
          <Col lg={4}>
            <Card className="dashboard-card">
              <CardBody>
                <div className="text-center">
                  {/* <div className="avatar-sm rounded-circle bg-info bg-soft mx-auto mb-3">
                    <i className="fas fa-wallet fa-2x text-info"></i>
                  </div> */}
                  <h4 className="card-title mb-2">Receivable Amount</h4>
                  <h2 className="text-info mb-0">
                    {receivableAmount.toLocaleString()}
                  </h2>
                  <p className="text-muted mt-2">
                    Total outstanding payments from all projects
                  </p>
                </div>
              </CardBody>
            </Card>
          </Col>
        </Row>

        <Row className="mb-4">
          <Col lg={6}>
            <OngoingProjects />
          </Col>
          <Col lg={6}>
            <HighPriorityLeads />
          </Col>
        </Row>
        {/* Quick Stats Row */}
        {/* <Row>
          <Col lg={3}>
            <Card className="bg-primary text-white">
              <CardBody>
                <div className="d-flex align-items-center">
                  <div className="flex-grow-1">
                    <h4 className="mb-0">{clients.length}</h4>
                    <span>Total Clients</span>
                  </div>
                  <div className="flex-shrink-0">
                    <i className="fas fa-users fa-2x"></i>
                  </div>
                </div>
              </CardBody>
            </Card>
          </Col>
          <Col lg={3}>
            <Card className="bg-success text-white">
              <CardBody>
                <div className="d-flex align-items-center">
                  <div className="flex-grow-1">
                    <h4 className="mb-0">{projects.length}</h4>
                    <span>Total Projects</span>
                  </div>
                  <div className="flex-shrink-0">
                    <i className="fas fa-briefcase fa-2x"></i>
                  </div>
                </div>
              </CardBody>
            </Card>
          </Col>
          <Col lg={3}>
            <Card className="bg-info text-white">
              <CardBody>
                <div className="d-flex align-items-center">
                  <div className="flex-grow-1">
                    <h4 className="mb-0">{clientLeads.length}</h4>
                    <span>Total Leads</span>
                  </div>
                  <div className="flex-shrink-0">
                    <i className="fas fa-list-alt fa-2x"></i>
                  </div>
                </div>
              </CardBody>
            </Card>
          </Col>
          <Col lg={3}>
            <Card className="bg-warning text-white">
              <CardBody>
                <div className="d-flex align-items-center">
                  <div className="flex-grow-1">
                    <h4 className="mb-0">
                      $
                      {fixedCosts
                        .reduce(
                          (sum, cost) => sum + parseFloat(cost.amount || 0),
                          0
                        )
                        .toLocaleString()}
                    </h4>
                    <span>Total Fixed Costs</span>
                  </div>
                  <div className="flex-shrink-0">
                    <i className="fas fa-receipt fa-2x"></i>
                  </div>
                </div>
              </CardBody>
            </Card>
          </Col>
        </Row> */}
      </div>
    </div>
  )
}

export default AdminDashboard
